/**
 * FIDDUX PDF Report Generator (Hub)
 * Aceita Markdown ou JSON, gera PDFs bilíngues com branding FIDDUX.
 * Hub único: Fontes & IA -> Conteúdo & Capa -> Preview & PDF -> Áudio.
 * Separa audiência Interno vs Cliente e gera rascunho por IA + áudio.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Switch,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import { collection, addDoc, query, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { DATABASE, STORAGE, FUNCTIONS } from '../../../Config/firebase';
import { toast } from 'react-toastify';
import html2pdf from 'html2pdf.js';
import { parseContent, applyLanguage } from '../../../Utils/pdfReportParser';
import { buildHtmlFromSchema } from '../../../Utils/pdfReportRenderer';
import { ensureSchemaHasPT } from '../../../Utils/pdfReportTranslate';
import { renderCharts, destroyCharts } from '../../../Utils/pdfReportCharts';
import { FIDDUX_BRANDING } from '../../../Utils/pdfReportBranding';
import {
  AUDIENCES,
  AUDIENCE_LABELS,
  COVER_PRESETS,
  AUDIO_PERSONA_EN,
  AUDIO_PERSONA_PT,
  NOTEBOOKLM_COPY_PREAMBLE_EN,
  NOTEBOOKLM_COPY_PREAMBLE_PT,
  buildEmptyTemplate,
  buildReportFilename,
  AUDIO_TTS_STYLES,
  DEFAULT_AUDIO_TTS_STYLE,
} from '../../../Utils/pdfReportTemplates';
import {
  loadLocalDraft,
  saveLocalDraft,
  clearLocalDraft,
  loadSourceHistory,
  upsertSourceHistory,
  touchSourceHistoryEntry,
  hashSources,
} from '../../../Utils/pdfReportDraftStorage';
import fidduxLogo from '../../../Assets/Images/Common/pdf_logo.png';

const REPORT_TYPES = [
  { value: 'analysis', label: 'Analysis' },
  { value: 'audit', label: 'Audit' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'research', label: 'Research' },
];

const SAMPLE_MARKDOWN = `# Executive Summary

Brazil faces fiscal challenges. **Deficit**: -1.8% PIB.

| Year | Value |
|------|-------|
| 2024 | 79% |
| 2025 | 82% |

## Key Findings

- Point one
- Point two
`;

const COVER_DEFAULTS = {
  title: '',
  title_pt: '',
  cover_subtitle: '',
  cover_subtitle_pt: '',
  prepared_by: '',
  prepared_by_pt: '',
  exclusive_partners: '',
  exclusive_partners_pt: '',
  cover_date: '{date} | Private & Confidential | Internal Use Only',
  cover_date_pt: '{date} | Confidencial | Uso Interno',
  footer_branding: '',
  footer_branding_pt: '',
  footer_minimal: true,
  logoOffset: -150,
  titleOffset: 200,
  detailsOffset: 88,
  dateOffset: 220,
};

export default function PDFReportGenerator() {
  const [tab, setTab] = useState(0);
  const [audience, setAudience] = useState(AUDIENCES.client);
  const [content, setContent] = useState(SAMPLE_MARKDOWN);
  const [inputType, setInputType] = useState('markdown');
  const [language, setLanguage] = useState('both');
  const [translateWhenPt, setTranslateWhenPt] = useState(true);
  const [reportType, setReportType] = useState('analysis');
  const [outputFormat] = useState('download');
  const [schema, setSchema] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [recentReports, setRecentReports] = useState([]);
  const [cover, setCover] = useState({ ...COVER_DEFAULTS });

  // IA
  const [aiSources, setAiSources] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [sourceHistory, setSourceHistory] = useState([]);
  const [draftRestored, setDraftRestored] = useState(false);
  const [duplicateSourceEntry, setDuplicateSourceEntry] = useState(null);
  const draftSaveTimerRef = useRef(null);

  // Áudio
  const [audioScript, setAudioScript] = useState('');
  const [audioScriptPt, setAudioScriptPt] = useState('');
  const [audioLang, setAudioLang] = useState('pt');
  const [audioStyle, setAudioStyle] = useState(DEFAULT_AUDIO_TTS_STYLE);
  const [audioResults, setAudioResults] = useState({ en: null, pt: null });
  const [audioGenerating, setAudioGenerating] = useState(null);
  const [audioSaving, setAudioSaving] = useState(false);

  const previewRef = useRef(null);
  const chartInstancesRef = useRef([]);

  const applyAudiencePreset = (aud) => {
    const preset = COVER_PRESETS[aud];
    if (!preset) return;
    setCover((c) => ({
      ...c,
      cover_subtitle: preset.cover_subtitle,
      cover_subtitle_pt: preset.cover_subtitle_pt,
      prepared_by: preset.prepared_by,
      prepared_by_pt: preset.prepared_by_pt,
      cover_date: preset.cover_date,
      cover_date_pt: preset.cover_date_pt,
      footer_branding: preset.footer_branding,
      footer_branding_pt: preset.footer_branding_pt,
      footer_minimal: preset.footer_minimal,
    }));
  };

  const handleAudienceChange = (_, aud) => {
    if (!aud) return;
    setAudience(aud);
    applyAudiencePreset(aud);
  };

  const mergeCoverIntoSchema = (parsed, coverOverride = null) => {
    const c = coverOverride ?? cover;
    const prev = parsed.report_meta || {};
    if (!parsed.report_meta) parsed.report_meta = {};
    const has = (v) => v != null && String(v).trim() !== '';
    parsed.report_meta = {
      ...prev,
      title: has(c.title) ? c.title : (prev.title || ''),
      title_pt: has(c.title_pt) ? c.title_pt : (prev.title_pt ?? prev.title ?? ''),
      cover_subtitle: has(c.cover_subtitle) ? c.cover_subtitle : (prev.cover_subtitle || ''),
      cover_subtitle_pt: has(c.cover_subtitle_pt) ? c.cover_subtitle_pt : (prev.cover_subtitle_pt ?? prev.cover_subtitle ?? ''),
      prepared_by: has(c.prepared_by) ? c.prepared_by : (prev.prepared_by || ''),
      prepared_by_pt: has(c.prepared_by_pt) ? c.prepared_by_pt : (prev.prepared_by_pt ?? prev.prepared_by ?? ''),
      exclusive_partners: has(c.exclusive_partners) ? c.exclusive_partners : (prev.exclusive_partners || ''),
      exclusive_partners_pt: has(c.exclusive_partners_pt) ? c.exclusive_partners_pt : (prev.exclusive_partners_pt ?? prev.exclusive_partners ?? ''),
      cover_date: has(c.cover_date) ? c.cover_date : (prev.cover_date ?? COVER_DEFAULTS.cover_date),
      cover_date_pt: has(c.cover_date_pt) ? c.cover_date_pt : (prev.cover_date_pt ?? COVER_DEFAULTS.cover_date_pt),
      footer_branding: has(c.footer_branding) ? c.footer_branding : (prev.footer_branding ?? ''),
      footer_minimal: c.footer_minimal ?? prev.footer_minimal ?? COVER_DEFAULTS.footer_minimal,
      audience: prev.audience || audience,
      coverLogoOffset: c.logoOffset ?? prev.coverLogoOffset ?? -150,
      coverTitleOffset: c.titleOffset ?? prev.coverTitleOffset ?? 200,
      coverDetailsOffset: c.detailsOffset ?? prev.coverDetailsOffset ?? 88,
      coverDateOffset: c.dateOffset ?? prev.coverDateOffset ?? 220,
    };
    return parsed;
  };

  const refreshPreview = (coverOverride = null) => {
    if (schema && previewRef.current) {
      destroyCharts(chartInstancesRef.current);
      chartInstancesRef.current = [];
      const merged = mergeCoverIntoSchema(JSON.parse(JSON.stringify(schema)), coverOverride);
      previewRef.current.innerHTML = buildHtmlFromSchema(merged, language === 'both' ? 'en' : language, fidduxLogo);
      chartInstancesRef.current = renderCharts(previewRef.current);
    }
  };

  useEffect(() => {
    loadRecentReports();
    loadSourceHistoryList();
    const draft = loadLocalDraft();
    if (draft?.aiSources || draft?.content) {
      if (draft.aiSources != null) setAiSources(draft.aiSources);
      if (draft.content != null) setContent(draft.content);
      if (draft.inputType) setInputType(draft.inputType);
      if (draft.audience) setAudience(draft.audience);
      if (draft.reportType) setReportType(draft.reportType);
      if (draft.language) setLanguage(draft.language);
      if (draft.translateWhenPt != null) setTranslateWhenPt(draft.translateWhenPt);
      if (draft.cover) setCover((c) => ({ ...c, ...draft.cover }));
      if (draft.audioScript != null) setAudioScript(draft.audioScript);
      if (draft.audioScriptPt != null) setAudioScriptPt(draft.audioScriptPt);
      if (draft.audioStyle) setAudioStyle(draft.audioStyle);
      if (draft.tab != null) setTab(draft.tab);
      if (draft.schema) {
        setSchema(draft.schema);
        setTimeout(() => {
          if (previewRef.current) {
            previewRef.current.innerHTML = buildHtmlFromSchema(
              draft.schema,
              draft.language === 'both' ? 'en' : (draft.language || 'en'),
              fidduxLogo
            );
          }
        }, 0);
      }
      setDraftRestored(true);
      toast.info('Rascunho restaurado automaticamente.');
    }
  }, []);

  // Auto-save do rascunho local (debounced) — sobrevive a reload da página.
  useEffect(() => {
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      saveLocalDraft({
        aiSources,
        content,
        inputType,
        audience,
        reportType,
        language,
        translateWhenPt,
        cover,
        audioScript,
        audioScriptPt,
        tab,
        schema,
        audioStyle,
      });
    }, 800);
    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    };
  }, [
    aiSources,
    content,
    inputType,
    audience,
    reportType,
    language,
    translateWhenPt,
    cover,
    audioScript,
    audioScriptPt,
    tab,
    schema,
    audioStyle,
  ]);

  // Detectar se estas sources já foram processadas pela IA antes.
  useEffect(() => {
    const hash = hashSources(aiSources);
    if (!hash || aiSources.trim().length < 20) {
      setDuplicateSourceEntry(null);
      return;
    }
    const match = sourceHistory.find((h) => h.sources_hash === hash && h.ai_generated);
    setDuplicateSourceEntry(match || null);
  }, [aiSources, sourceHistory]);

  const loadSourceHistoryList = async () => {
    try {
      const items = await loadSourceHistory(25);
      setSourceHistory(items);
    } catch (e) {
      console.warn('Falha ao carregar histórico de sources:', e);
    }
  };

  // Alinhar idioma do áudio ao idioma de saída do PDF quando fizer sentido.
  useEffect(() => {
    if (language === 'en') setAudioLang('en');
    else if (language === 'pt' || language === 'both') setAudioLang('pt');
  }, [language]);

  const loadRecentReports = async () => {
    try {
      const q = query(
        collection(DATABASE, 'admin_pdf_reports'),
        orderBy('generated_at', 'desc'),
        limit(10)
      );
      const snap = await getDocs(q);
      setRecentReports(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.warn('Could not load recent reports:', e);
    }
  };

  const syncCoverFromSchema = (parsed) => {
    const m = parsed.report_meta || {};
    setCover((prev) => ({
      ...prev,
      title: m.title || prev.title || '',
      title_pt: m.title_pt ?? m.title ?? prev.title_pt ?? '',
      cover_subtitle: m.cover_subtitle ?? prev.cover_subtitle ?? '',
      cover_subtitle_pt: m.cover_subtitle_pt ?? m.cover_subtitle ?? prev.cover_subtitle_pt ?? '',
      prepared_by: m.prepared_by ?? prev.prepared_by ?? '',
      prepared_by_pt: m.prepared_by_pt ?? m.prepared_by ?? prev.prepared_by_pt ?? '',
      exclusive_partners: m.exclusive_partners ?? prev.exclusive_partners ?? '',
      exclusive_partners_pt: m.exclusive_partners_pt ?? m.exclusive_partners ?? prev.exclusive_partners_pt ?? '',
      footer_branding: m.footer_branding ?? prev.footer_branding ?? '',
      footer_branding_pt: m.footer_branding_pt ?? m.footer_branding ?? prev.footer_branding_pt ?? '',
    }));
  };

  const syncAudioFromSchema = (parsed) => {
    if (parsed.audio_script != null) setAudioScript(parsed.audio_script || '');
    if (parsed.audio_script_pt != null) setAudioScriptPt(parsed.audio_script_pt || '');
    // Preferir PT como idioma do áudio quando o script PT existir.
    if (parsed.audio_script_pt && String(parsed.audio_script_pt).trim().length > 20) {
      setAudioLang('pt');
    }
  };

  const handleParse = () => {
    setError(null);
    try {
      const parsed = parseContent(content, inputType);
      parsed.report_meta.document_type = reportType;
      parsed.report_meta.date = parsed.report_meta.date || new Date().toISOString().split('T')[0];
      parsed.report_meta.audience = parsed.report_meta.audience || audience;
      syncCoverFromSchema(parsed);
      syncAudioFromSchema(parsed);
      const merged = mergeCoverIntoSchema(parsed);
      setSchema(merged);
      if (previewRef.current) {
        previewRef.current.innerHTML = buildHtmlFromSchema(merged, language === 'both' ? 'en' : language, fidduxLogo);
      }
    } catch (e) {
      setError(e.message);
      setSchema(null);
    }
  };

  const handleLoadEmptyTemplate = () => {
    const tpl = buildEmptyTemplate(audience);
    setContent(JSON.stringify(tpl, null, 2));
    setInputType('json');
    setSchema(mergeCoverIntoSchema(JSON.parse(JSON.stringify(tpl))));
    setTab(1);
    toast.info(
      `Template JSON vazio (${AUDIENCE_LABELS[audience]}) carregado na aba Conteúdo — preencha manualmente, sem IA. As sources acima não foram alteradas.`
    );
  };

  const handleSaveSourcesToHistory = async () => {
    if (!aiSources || aiSources.trim().length < 20) {
      toast.error('Cole as sources antes de guardar no histórico.');
      return;
    }
    try {
      await upsertSourceHistory({ sources: aiSources, audience, reportType, aiGenerated: false });
      await loadSourceHistoryList();
      toast.success('Sources guardadas no histórico.');
    } catch (e) {
      toast.error(e?.message || 'Falha ao guardar sources.');
    }
  };

  const handleLoadSourceFromHistory = async (entry) => {
    if (!entry?.sources) return;
    setAiSources(entry.sources);
    if (entry.audience) {
      setAudience(entry.audience);
      applyAudiencePreset(entry.audience);
    }
    if (entry.report_type) setReportType(entry.report_type);
    setTab(0);
    try {
      await touchSourceHistoryEntry(entry.id);
      await loadSourceHistoryList();
    } catch {
      /* ignore */
    }
    toast.success('Sources carregadas do histórico.');
  };

  const handleClearDraft = () => {
    clearLocalDraft();
    setDraftRestored(false);
    toast.info('Rascunho local limpo. O histórico no Firebase permanece.');
  };

  const handleGenerateWithAI = async () => {
    setError(null);
    if (!aiSources || aiSources.trim().length < 20) {
      toast.error('Cole as sources (texto, links ou notas) antes de gerar.');
      return;
    }
    setAiGenerating(true);
    try {
      const fn = httpsCallable(FUNCTIONS, 'generateReportFromSources');
      const res = await fn({ sources: aiSources, audience, reportType, language });
      const data = res.data || {};
      if (!data.success || !data.schema) throw new Error('IA não retornou um report válido.');
      const aiSchema = data.schema;
      const jsonStr = JSON.stringify(aiSchema, null, 2);
      setContent(jsonStr);
      setInputType('json');
      aiSchema.report_meta = aiSchema.report_meta || {};
      aiSchema.report_meta.document_type = aiSchema.report_meta.document_type || reportType;
      aiSchema.report_meta.audience = audience;
      syncCoverFromSchema(aiSchema);
      syncAudioFromSchema(aiSchema);
      const merged = mergeCoverIntoSchema(aiSchema);
      setSchema(merged);
      if (previewRef.current) {
        previewRef.current.innerHTML = buildHtmlFromSchema(merged, language === 'both' ? 'en' : language, fidduxLogo);
      }
      toast.success('Rascunho gerado pela IA. Revise antes de gerar o PDF.');
      try {
        await upsertSourceHistory({
          sources: aiSources,
          audience,
          reportType,
          aiGenerated: true,
          reportTitle: aiSchema.report_meta?.title || '',
          reportTitlePt: aiSchema.report_meta?.title_pt || '',
        });
        await loadSourceHistoryList();
      } catch (histErr) {
        console.warn('Histórico de sources não salvo:', histErr);
      }
      setTab(1);
    } catch (e) {
      const msg = e?.message || 'Falha ao gerar com IA';
      setError(msg);
      toast.error(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  const buildReportMetaForSave = (applied, lang) => {
    const meta = applied?.report_meta || {};
    return {
      title: meta.title || cover.title || 'FIDDUX Report',
      title_pt: meta.title_pt || cover.title_pt || meta.title || '',
      audience: meta.audience || audience,
      report_type: meta.document_type || reportType,
      language: lang,
      date: meta.date || new Date().toISOString().split('T')[0],
    };
  };

  const handleGenerate = async (forceOutput) => {
    setError(null);
    setGenerating(true);
    try {
      let parsed;
      try {
        parsed = parseContent(content, inputType);
      } catch (parseErr) {
        setError(parseErr.message);
        toast.error(parseErr.message);
        return;
      }
      parsed.report_meta.document_type = reportType;
      parsed.report_meta.date = parsed.report_meta.date || new Date().toISOString().split('T')[0];
      parsed.report_meta.audience = parsed.report_meta.audience || audience;
      syncCoverFromSchema(parsed);
      syncAudioFromSchema(parsed);
      const merged = mergeCoverIntoSchema(parsed);
      setSchema(merged);

      const effectiveOutput = forceOutput || outputFormat;
      const langs = language === 'both' ? ['pt', 'en'] : [language];
      for (let i = 0; i < langs.length; i++) {
        await generatePdfWithSchema(merged, langs[i], forceOutput);
        if (i < langs.length - 1 && effectiveOutput === 'download') {
          await new Promise((r) => setTimeout(r, 800));
        }
      }
      if (previewRef.current) {
        previewRef.current.innerHTML = buildHtmlFromSchema(merged, language === 'both' ? 'en' : language, fidduxLogo);
        previewRef.current.style.minHeight = '';
        previewRef.current.style.width = '';
      }
      toast.success(`PDF(s) gerado(s) com sucesso!`);
    } catch (e) {
      setError(e.message || 'Erro ao gerar PDF');
      toast.error(e.message || 'Erro ao gerar PDF');
    } finally {
      setGenerating(false);
    }
  };

  const generatePdfWithSchema = async (parsedSchema, lang, forceOutput) => {
    const effectiveOutput = forceOutput || outputFormat;
    const schemaForLang = lang === 'pt' && translateWhenPt
      ? await ensureSchemaHasPT(JSON.parse(JSON.stringify(parsedSchema)))
      : parsedSchema;
    const applied = applyLanguage(schemaForLang, lang);
    const html = buildHtmlFromSchema(schemaForLang, lang, fidduxLogo);

    const filename = buildReportFilename({
      title: applied.report_meta?.title_pt && lang === 'pt'
        ? applied.report_meta.title_pt
        : (applied.report_meta?.title || cover.title || ''),
      audience: applied.report_meta?.audience || audience,
      date: applied.report_meta?.date || new Date().toISOString().split('T')[0],
      lang,
      ext: 'pdf',
    });

    if (!previewRef.current) throw new Error('Preview não disponível');
    const el = previewRef.current;
    destroyCharts(chartInstancesRef.current);
    chartInstancesRef.current = [];
    el.innerHTML = html;
    el.style.minHeight = '297mm';
    el.style.width = '210mm';
    el.style.border = 'none';
    el.style.padding = '0';
    el.scrollIntoView({ behavior: 'instant', block: 'start' });

    chartInstancesRef.current = renderCharts(el);
    await new Promise((r) => setTimeout(r, 800));

    // REGRA PERMANENTE: margem 0, conteúdo centralizado. NUNCA alterar.
    const opt = {
      margin: [0, 0, 0, 0],
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, logging: false, scrollX: 0, scrollY: 0 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: ['.fiddux-table-wrapper', '.fiddux-table', '.fiddux-chart-container', '.fiddux-highlight-box', '.fiddux-subsection-gap'] },
    };

    try {
      const pdf = await html2pdf().set(opt).from(previewRef.current).toPdf().get('pdf');
      const totalPages = pdf.internal.getNumberOfPages();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const footerTop = pageHeight - 24;

      const meta = applied?.report_meta || schemaForLang?.report_meta || parsedSchema?.report_meta || {};
      const footerBranding = (lang === 'pt' ? meta.footer_branding_pt : null) || meta.footer_branding || 'Internal and Confidential';
      const footerMinimal = meta.footer_minimal === true || meta.footer?.no_local_references === true;

      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, 24, 'F');
        pdf.setDrawColor(218, 218, 218);
        pdf.setLineWidth(0.3);
        pdf.line(20, footerTop + 1, pageWidth - 20, footerTop + 1);

        pdf.setFontSize(7);
        pdf.setTextColor(90, 90, 90);
        pdf.text(footerBranding, 20, footerTop + 7);
        if (!footerMinimal) {
          pdf.text('info@fiddux.com', 20, footerTop + 11);
          pdf.text('Joe 2, Harjumaa, 10151, Tallinn, Estonia', 20, footerTop + 15);
        }

        pdf.setFontSize(8);
        pdf.setTextColor(80, 80, 80);
        pdf.text(`${i} / ${totalPages}`, pageWidth - 20, footerTop + 11, { align: 'right' });
      }

      const blob = pdf.output('blob');

      if (effectiveOutput === 'download') {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      } else if (effectiveOutput === 'save_s3') {
        const storageRef = ref(STORAGE, `reports/${filename}`);
        await uploadBytes(storageRef, blob);
        const pdfUrl = await getDownloadURL(storageRef);
        const metaForSave = buildReportMetaForSave(applied, lang);
        await addDoc(collection(DATABASE, 'admin_pdf_reports'), {
          filename,
          pdf_url: pdfUrl,
          ...metaForSave,
          file_size_mb: (blob.size / 1024 / 1024).toFixed(2),
          generated_at: Timestamp.now(),
        });
        toast.success(`PDF salvo: ${filename}`);
        loadRecentReports();
      }
      return blob;
    } catch (e) {
      throw e;
    } finally {
      if (previewRef.current) {
        previewRef.current.style.minHeight = '';
        previewRef.current.style.width = '';
        previewRef.current.style.border = '';
        previewRef.current.style.padding = '';
      }
    }
  };

  // ----- Áudio -----
  const currentAudioScript = audioLang === 'pt' ? audioScriptPt : audioScript;
  const currentPersona = audioLang === 'pt' ? NOTEBOOKLM_COPY_PREAMBLE_PT : NOTEBOOKLM_COPY_PREAMBLE_EN;

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || '');
      toast.success(`${label} copiado!`);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  const handleCopyNotebookLM = () => {
    const text = `${currentPersona}\n\n---\n\nSCRIPT (do not mention sources or names from inputs):\n\n${currentAudioScript || ''}`;
    copyToClipboard(text, 'Script + instruções pro NotebookLM');
  };

  const handleGenerateAudio = async (langOverride) => {
    const lang = langOverride || audioLang;
    const script = lang === 'pt' ? audioScriptPt : audioScript;
    const langLabel = lang === 'pt' ? 'português (BR)' : 'inglês';
    if (!script || script.trim().length < 20) {
      toast.error(
        lang === 'pt'
          ? 'Script PT vazio. Gere o report pela IA (idioma PT ou Ambos) ou escreva o script em português.'
          : 'Script EN vazio. Gere o report pela IA ou escreva o script em inglês.'
      );
      return;
    }
    setAudioGenerating(lang);
    setError(null);
    try {
      const fn = httpsCallable(FUNCTIONS, 'generateReportAudio');
      const res = await fn({ script, language: lang, audioStyle });
      const data = res.data || {};
      if (!data.success || !data.audioBase64) throw new Error('TTS não retornou áudio.');
      const byteChars = atob(data.audioBase64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      const blob = new Blob([bytes], { type: data.mimeType || 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      setAudioResults((prev) => {
        if (prev[lang]?.url) URL.revokeObjectURL(prev[lang].url);
        return { ...prev, [lang]: { url, blob } };
      });
      setAudioLang(lang);
      const styleLabel = AUDIO_TTS_STYLES.find((s) => s.value === (data.audioStyle || audioStyle))?.label || data.audioStyle;
      const extra = data.fallbackFrom ? ' (fallback clássico — Gemini indisponível)' : '';
      toast.success(`Áudio ${styleLabel} em ${langLabel} gerado (${data.chunks || 1} parte(s))${extra}.`);
    } catch (e) {
      const msg = e?.message || 'Falha ao gerar áudio';
      setError(msg);
      toast.error(msg);
    } finally {
      setAudioGenerating(null);
    }
  };

  const audioFilename = (lang) =>
    buildReportFilename({
      title: (lang === 'pt' ? cover.title_pt : cover.title) || schema?.report_meta?.title || 'Report',
      audience,
      date: schema?.report_meta?.date,
      lang,
      ext: 'mp3',
    });

  const handleDownloadAudio = (lang) => {
    const item = audioResults[lang];
    if (!item?.blob) return;
    const a = document.createElement('a');
    a.href = item.url;
    a.download = audioFilename(lang);
    a.click();
  };

  const handleSaveAudioToStorage = async (lang) => {
    const item = audioResults[lang];
    if (!item?.blob) return;
    setAudioSaving(true);
    try {
      const filename = audioFilename(lang);
      const storageRef = ref(STORAGE, `reports/${filename}`);
      await uploadBytes(storageRef, item.blob);
      const url = await getDownloadURL(storageRef);
      await addDoc(collection(DATABASE, 'admin_pdf_reports'), {
        filename,
        audio_url: url,
        title: cover.title || schema?.report_meta?.title || 'FIDDUX Report',
        title_pt: cover.title_pt || schema?.report_meta?.title_pt || '',
        audience,
        report_type: reportType,
        language: lang,
        date: schema?.report_meta?.date || new Date().toISOString().split('T')[0],
        kind: 'audio',
        file_size_mb: (item.blob.size / 1024 / 1024).toFixed(2),
        generated_at: Timestamp.now(),
      });
      toast.success(`Áudio salvo: ${filename}`);
      loadRecentReports();
    } catch (e) {
      toast.error(e?.message || 'Falha ao salvar áudio');
    } finally {
      setAudioSaving(false);
    }
  };

  useEffect(() => {
    if (schema && previewRef.current) {
      destroyCharts(chartInstancesRef.current);
      chartInstancesRef.current = [];
      const merged = mergeCoverIntoSchema(JSON.parse(JSON.stringify(schema)));
      previewRef.current.innerHTML = buildHtmlFromSchema(merged, language === 'both' ? 'en' : language, fidduxLogo);
      chartInstancesRef.current = renderCharts(previewRef.current);
    }
    return () => destroyCharts(chartInstancesRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, language, cover]);

  const tabSx = { textTransform: 'none', fontWeight: 600 };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: FIDDUX_BRANDING.primary }}>
        PDF Report Generator
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Hub institucional: gere o rascunho por IA a partir das sources, padronize por audiência (Interno/Cliente), exporte o PDF e produza o áudio.
      </Typography>

      {/* Audiência + opções globais */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
              Audiência
            </Typography>
            <ToggleButtonGroup value={audience} exclusive onChange={handleAudienceChange} size="small">
              <ToggleButton value={AUDIENCES.internal}>Interno</ToggleButton>
              <ToggleButton value={AUDIENCES.client}>Cliente</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <ToggleButtonGroup value={inputType} exclusive onChange={(_, v) => v && setInputType(v)} size="small">
            <ToggleButton value="markdown">Markdown</ToggleButton>
            <ToggleButton value="json">JSON</ToggleButton>
          </ToggleButtonGroup>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Idioma de saída</InputLabel>
            <Select value={language} label="Idioma de saída" onChange={(e) => setLanguage(e.target.value)}>
              <MenuItem value="pt">Português (BR)</MenuItem>
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="both">Ambos (PT + EN)</MenuItem>
            </Select>
          </FormControl>
          {(language === 'pt' || language === 'both') && (
            <FormControlLabel
              control={<Switch checked={translateWhenPt} onChange={(e) => setTranslateWhenPt(e.target.checked)} size="small" />}
              label="Traduzir para PT (API)"
            />
          )}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={reportType} label="Tipo" onChange={(e) => setReportType(e.target.value)}>
              {REPORT_TYPES.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab sx={tabSx} icon={<AutoAwesomeIcon />} iconPosition="start" label="1. Fontes & IA" />
        <Tab sx={tabSx} icon={<RefreshIcon />} iconPosition="start" label="2. Conteúdo & Capa" />
        <Tab sx={tabSx} icon={<PictureAsPdfIcon />} iconPosition="start" label="3. Preview & PDF" />
        <Tab sx={tabSx} icon={<GraphicEqIcon />} iconPosition="start" label="4. Áudio" />
      </Tabs>

      {/* TAB 1 — Fontes & IA */}
      <Box hidden={tab !== 0}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Gerar com IA ({AUDIENCE_LABELS[audience]})
          </Typography>
          <Typography variant="caption" sx={{ mb: 1.5, display: 'block', color: 'text.secondary' }}>
            Cole as sources (texto, links, transcrições do YouTube, notas). A IA monta o report no template padrão da audiência, bilíngue (EN+PT), e já gera o script de áudio. Revise antes de exportar.
            {' '}O rascunho (sources + conteúdo + capa) é <strong>salvo automaticamente no navegador</strong> — sobrevive a reload.
          </Typography>
          {duplicateSourceEntry && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              Estas sources já foram processadas pela IA
              {duplicateSourceEntry.last_ai_at?.toDate
                ? ` em ${duplicateSourceEntry.last_ai_at.toDate().toLocaleDateString('pt-BR')}`
                : ''}
              {duplicateSourceEntry.report_title ? ` (“${duplicateSourceEntry.report_title}”)` : ''}.
              Você pode reutilizar o rascunho restaurado ou gerar de novo (consome IA outra vez).
            </Alert>
          )}
          {draftRestored && (
            <Chip size="small" color="info" label="Rascunho restaurado desta sessão" sx={{ mb: 1 }} />
          )}
          <TextField
            fullWidth
            multiline
            rows={12}
            value={aiSources}
            onChange={(e) => setAiSources(e.target.value)}
            placeholder="Cole aqui suas sources..."
            size="small"
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={aiGenerating ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
              onClick={handleGenerateWithAI}
              disabled={aiGenerating}
              sx={{ bgcolor: FIDDUX_BRANDING.primary, '&:hover': { bgcolor: FIDDUX_BRANDING.tertiary || '#009658' } }}
            >
              {aiGenerating ? 'Gerando rascunho...' : 'Gerar rascunho com IA'}
            </Button>
            <Button variant="outlined" onClick={handleLoadEmptyTemplate}>
              Template JSON vazio (sem IA)
            </Button>
            <Button variant="outlined" startIcon={<SaveIcon />} onClick={handleSaveSourcesToHistory}>
              Guardar sources
            </Button>
            <Button variant="text" size="small" onClick={handleClearDraft}>
              Limpar rascunho local
            </Button>
          </Box>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
            <strong>Template JSON vazio:</strong> pula a IA e carrega seções em branco na aba Conteúdo para preenchimento manual. Não apaga as sources acima.
          </Typography>
        </Paper>

        {sourceHistory.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon fontSize="small" /> Histórico de sources
            </Typography>
            <Typography variant="caption" sx={{ mb: 1.5, display: 'block', color: 'text.secondary' }}>
              Sources já usadas ficam guardadas no Firebase. Clique para recarregar sem colar de novo.
            </Typography>
            <List dense disablePadding>
              {sourceHistory.map((entry) => (
                <React.Fragment key={entry.id}>
                  <ListItem
                    sx={{ px: 0, alignItems: 'flex-start' }}
                    secondaryAction={
                      <IconButton edge="end" title="Carregar sources" onClick={() => handleLoadSourceFromHistory(entry)}>
                        <RestoreIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Typography variant="body2" component="span">
                            {entry.report_title || entry.sources_preview || 'Sources'}
                          </Typography>
                          {entry.ai_generated && <Chip size="small" label="IA gerada" color="success" variant="outlined" />}
                          <Chip size="small" label={AUDIENCE_LABELS[entry.audience] || entry.audience} variant="outlined" />
                        </Box>
                      }
                      secondary={
                        <>
                          {entry.sources_preview}
                          {entry.last_used_at?.toDate && (
                            <> · Último uso: {entry.last_used_at.toDate().toLocaleString('pt-BR')}</>
                          )}
                          {entry.use_count > 1 && <> · {entry.use_count}× usado</>}
                        </>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* TAB 2 — Conteúdo & Capa */}
      <Box hidden={tab !== 1}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>Capa</Typography>
          <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary' }}>Preenchido automaticamente pelo JSON/IA. Edite aqui para personalizar. Use Enter para quebrar em duas linhas.</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Título" value={cover.title} onChange={(e) => setCover((c) => ({ ...c, title: e.target.value }))} size="small" fullWidth multiline minRows={1} />
            <TextField label="Subtítulo" value={cover.cover_subtitle} onChange={(e) => setCover((c) => ({ ...c, cover_subtitle: e.target.value }))} size="small" fullWidth multiline minRows={1} />
            <TextField label="Prepared by" value={cover.prepared_by} onChange={(e) => setCover((c) => ({ ...c, prepared_by: e.target.value }))} size="small" fullWidth multiline minRows={2} placeholder="Linha 1&#10;Linha 2" />
            <TextField label="Exclusive partners" value={cover.exclusive_partners} onChange={(e) => setCover((c) => ({ ...c, exclusive_partners: e.target.value }))} size="small" fullWidth multiline minRows={1} />
            <TextField label="Data e confidencial" value={cover.cover_date ?? COVER_DEFAULTS.cover_date} onChange={(e) => setCover((c) => ({ ...c, cover_date: e.target.value }))} size="small" fullWidth multiline minRows={1} placeholder="{date} = data do relatório" helperText="Use {date} para a data automática" sx={{ gridColumn: '1 / -1' }} />
            <TextField label="Rodapé" value={cover.footer_branding ?? COVER_DEFAULTS.footer_branding} onChange={(e) => setCover((c) => ({ ...c, footer_branding: e.target.value }))} size="small" fullWidth multiline minRows={1} placeholder="Texto exibido no rodapé de cada página" sx={{ gridColumn: '1 / -1' }} />
            <FormControlLabel
              control={<Switch checked={cover.footer_minimal ?? COVER_DEFAULTS.footer_minimal} onChange={(e) => setCover((c) => ({ ...c, footer_minimal: e.target.checked }))} />}
              label="Rodapé minimal (sem email e endereço)"
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
          <Typography variant="caption" sx={{ mt: 2, mb: 1.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>Posicionamento (px) — clique Preview antes de ajustar</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
            {[
              { key: 'logoOffset', label: 'Logo', hint: 'negativo = subir', default: -150, step: 10 },
              { key: 'titleOffset', label: 'Título', hint: '', default: 200, step: 5 },
              { key: 'detailsOffset', label: 'Bloco detalhes', hint: 'espaço título→detalhes', default: 88, step: 8 },
              { key: 'dateOffset', label: 'Data/confidencial', hint: 'mover linha', default: 220, step: 5 },
            ].map(({ key, label, hint, default: def, step }) => (
              <Paper key={key} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={() => { const v = (cover[key] ?? def) - step; const newCover = { ...cover, [key]: v }; setCover(newCover); refreshPreview(newCover); }} title={`${label} subir`} sx={{ bgcolor: 'action.hover' }}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <TextField type="number" label={label} value={cover[key] ?? def} onChange={(e) => { const v = parseInt(e.target.value, 10); const newCover = { ...cover, [key]: isNaN(v) ? def : v }; setCover(newCover); refreshPreview(newCover); }} size="small" fullWidth helperText={hint || undefined} sx={{ '& .MuiInputBase-input': { textAlign: 'center' } }} />
                <IconButton size="small" onClick={() => { const v = (cover[key] ?? def) + step; const newCover = { ...cover, [key]: v }; setCover(newCover); refreshPreview(newCover); }} title={`${label} descer`} sx={{ bgcolor: 'action.hover' }}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Box>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" onClick={() => refreshPreview()}>Atualizar preview</Button>
            <Button size="small" onClick={() => { setCover({ ...COVER_DEFAULTS }); refreshPreview(COVER_DEFAULTS); }}>Restaurar padrão</Button>
          </Box>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Conteúdo</Typography>
          <TextField
            fullWidth
            multiline
            rows={14}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={inputType === 'json' ? '{"report_meta": {...}, "sections": [...]}' : '# Título\n\nTexto...'}
            variant="outlined"
            size="small"
            sx={{ fontFamily: inputType === 'json' ? 'monospace' : 'inherit' }}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleParse}>Preview</Button>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} size="small">
              Upload JSON
              <input type="file" hidden accept=".json,application/json" onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const r = new FileReader();
                  r.onload = () => {
                    const str = String(r.result);
                    setContent(str);
                    setInputType('json');
                    try {
                      const json = JSON.parse(str);
                      const m = json.report_meta || {};
                      if (m.audience) setAudience(m.audience);
                      if (json.audio_script != null) setAudioScript(json.audio_script || '');
                      if (json.audio_script_pt != null) setAudioScriptPt(json.audio_script_pt || '');
                      if (m.title || m.cover_subtitle || m.prepared_by || m.exclusive_partners || m.cover_date || m.footer_branding != null || m.footer_minimal != null) {
                        setCover((prev) => ({
                          ...prev,
                          title: m.title ?? prev.title,
                          cover_subtitle: m.cover_subtitle ?? prev.cover_subtitle,
                          prepared_by: m.prepared_by ?? prev.prepared_by,
                          exclusive_partners: m.exclusive_partners ?? prev.exclusive_partners,
                          cover_date: m.cover_date ?? prev.cover_date,
                          footer_branding: m.footer_branding ?? prev.footer_branding,
                          footer_minimal: m.footer_minimal ?? prev.footer_minimal,
                        }));
                      }
                    } catch (_) {}
                    toast.success('JSON carregado');
                  };
                  r.readAsText(file, 'UTF-8');
                }
                e.target.value = '';
              }} />
            </Button>
            <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={() => setTab(2)} sx={{ bgcolor: FIDDUX_BRANDING.primary, '&:hover': { bgcolor: FIDDUX_BRANDING.tertiary || '#009658' } }}>
              Ir para Preview & PDF
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* TAB 3 — Preview & PDF (preview SEMPRE montado) */}
      <Box hidden={tab !== 2}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
              onClick={() => handleGenerate('download')}
              disabled={generating}
              sx={{ bgcolor: FIDDUX_BRANDING.primary, '&:hover': { bgcolor: FIDDUX_BRANDING.tertiary || '#009658' } }}
            >
              {generating ? 'Gerando...' : 'Baixar PDF'}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={generating ? <CircularProgress size={18} /> : <SaveIcon />}
              onClick={() => handleGenerate('save_s3')}
              disabled={generating}
            >
              {generating ? 'Salvando...' : 'Salvar na biblioteca'}
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleParse}>Atualizar preview</Button>
            <Chip size="small" label={`Audiência: ${AUDIENCE_LABELS[audience]}`} />
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
        </Paper>
      </Box>

      {/* Preview sempre montado (apenas escondido fora da aba 3) para a geração funcionar */}
      <Paper sx={{ p: 2, mt: 2, overflow: 'auto', display: tab === 2 ? 'block' : 'none' }}>
        <Box
          ref={previewRef}
          sx={{ minHeight: 360, bgcolor: '#fff', p: 2, border: '1px solid #e2e8f0', borderRadius: 1, '& .fiddux-pdf-root': { fontFamily: 'Inter, sans-serif' } }}
        />
      </Paper>

      {/* TAB 4 — Áudio */}
      <Box hidden={tab !== 3}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Áudio do report</Typography>
          <Typography variant="caption" sx={{ mb: 1.5, display: 'block', color: 'text.secondary' }}>
            Caminho recomendado: <strong>NotebookLM</strong> (grátis, áudio em formato podcast, melhor qualidade).
            Edite/gere o script abaixo, copie e cole no NotebookLM. O TTS integrado é só um <strong>rascunho rápido</strong>.
            Material proprietário Fiddux: o script <strong>nunca cita sources, links ou nomes de terceiros</strong>.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                Idioma do script (edição)
              </Typography>
              <ToggleButtonGroup
                value={audioLang}
                exclusive
                onChange={(_, v) => v && setAudioLang(v)}
                size="small"
              >
                <ToggleButton value="pt">Português (BR)</ToggleButton>
                <ToggleButton value="en">English</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Chip
              size="small"
              variant="outlined"
              label={`Script PT: ${(audioScriptPt || '').trim().length} chars`}
              color={audioScriptPt?.trim().length > 20 ? 'success' : 'default'}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Script EN: ${(audioScript || '').trim().length} chars`}
              color={audioScript?.trim().length > 20 ? 'success' : 'default'}
            />
          </Box>
          {!audioScriptPt?.trim() && audioScript?.trim() && (
            <Alert severity="info" sx={{ mb: 2 }}>
              O script em português ainda está vazio. Gere o report pela IA com idioma <strong>PT</strong> ou <strong>Ambos</strong>, ou cole/edite o texto PT abaixo.
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={8}
            label={audioLang === 'pt' ? 'Script do áudio (Português BR)' : 'Script do áudio (English)'}
            value={currentAudioScript}
            onChange={(e) => (audioLang === 'pt' ? setAudioScriptPt(e.target.value) : setAudioScript(e.target.value))}
            size="small"
            placeholder="Gere o report pela IA para preencher os scripts PT e EN automaticamente."
          />

          {/* CAMINHO PRINCIPAL — NotebookLM (destaque, largura total) */}
          <Paper
            variant="outlined"
            sx={{ p: 2, mt: 2, borderColor: FIDDUX_BRANDING.primary, borderWidth: 2, bgcolor: 'rgba(0,150,88,0.04)' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>🎧 Recomendado — NotebookLM (grátis)</Typography>
              <Chip size="small" color="success" label="Melhor qualidade" />
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
              Copie o script + instruções e cole no NotebookLM (formato podcast com dois apresentadores).
              O texto copiado já inclui a regra de não citar fontes nem nomes.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyNotebookLM}
                sx={{ bgcolor: FIDDUX_BRANDING.primary, '&:hover': { bgcolor: FIDDUX_BRANDING.tertiary || '#009658' } }}
              >
                Copiar script pro NotebookLM
              </Button>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(currentPersona, 'Persona')}>
                Copiar só a persona
              </Button>
              <Button
                variant="text"
                size="small"
                href="https://notebooklm.google.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir NotebookLM ↗
              </Button>
            </Box>
          </Paper>

          {/* SECUNDÁRIO — TTS integrado (rascunho rápido) */}
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Rascunho rápido — TTS integrado (opcional)
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}>
              Voz única gerada na hora (sem sair do site). Não tem a qualidade do NotebookLM — use só para conferir o texto.
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                Estilo
              </Typography>
              <ToggleButtonGroup
                value={audioStyle}
                exclusive
                onChange={(_, v) => v && setAudioStyle(v)}
                size="small"
                sx={{ flexWrap: 'wrap' }}
              >
                {AUDIO_TTS_STYLES.map((s) => (
                  <ToggleButton key={s.value} value={s.value} sx={{ textTransform: 'none' }}>
                    {s.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                {AUDIO_TTS_STYLES.find((s) => s.value === audioStyle)?.hint}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={audioGenerating === 'pt' ? <CircularProgress size={16} /> : <GraphicEqIcon />}
                  onClick={() => handleGenerateAudio('pt')}
                  disabled={!!audioGenerating}
                  sx={{ bgcolor: FIDDUX_BRANDING.primary, '&:hover': { bgcolor: FIDDUX_BRANDING.tertiary || '#009658' } }}
                >
                  {audioGenerating === 'pt' ? 'Gerando PT...' : 'Gerar TTS (PT)'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={audioGenerating === 'en' ? <CircularProgress size={16} /> : <GraphicEqIcon />}
                  onClick={() => handleGenerateAudio('en')}
                  disabled={!!audioGenerating}
                >
                  {audioGenerating === 'en' ? 'Gerando EN...' : 'Gerar TTS (EN)'}
                </Button>
              </Box>
              {(['pt', 'en']).map((lang) => {
                const item = audioResults[lang];
                if (!item?.url) return null;
                const label = lang === 'pt' ? 'Português (BR)' : 'English';
                return (
                  <Box key={lang} sx={{ mt: lang === 'pt' ? 0 : 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>{label}</Typography>
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <audio controls src={item.url} style={{ width: '100%', marginBottom: 8 }} />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleDownloadAudio(lang)}>
                        Baixar MP3
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={audioSaving ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                        onClick={() => handleSaveAudioToStorage(lang)}
                        disabled={audioSaving}
                      >
                        Salvar no Storage
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Paper>
        </Paper>
      </Box>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Relatórios recentes</Typography>
          <Button size="small" href="/admin/report-library">Abrir Biblioteca</Button>
        </Box>
        {recentReports.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nenhum relatório gerado ainda.</Typography>
        ) : (
          <List dense>
            {recentReports.map((r) => (
              <React.Fragment key={r.id}>
                <ListItem>
                  <ListItemText
                    primary={r.title || r.filename}
                    secondary={`${AUDIENCE_LABELS[r.audience] || r.audience || '-'} • ${r.report_type || '-'} • ${r.language?.toUpperCase?.() || '-'} • ${r.generated_at?.toDate?.()?.toLocaleString?.() || '-'}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton href={r.pdf_url || r.audio_url} target="_blank" rel="noopener noreferrer" size="small" title="Abrir" disabled={!r.pdf_url && !r.audio_url}>
                      <DownloadIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
}
